import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import type { OmenCtx } from '../omens/common.ts';
import { GlowLayer } from './glow.ts';
import { captureSky, paintHour, type Sky } from './shore.ts';

/**
 * The night of the bell at the bastion (El Párpado 4, docs/SAGAS.md): the group's own bastion under the Marea at night.
 * Dusk deepens into night as the fight goes on; at dawn the sky warms as the Marea thins out. Both are the board's own
 * lights repainted (as La Orilla's nights): no lights added (docs/RENDIMIENTO.md).
 *
 * The allies the group earned come in one at a time, each with their way in: Gaspar at the gate in a pale cold light,
 * a line of lanterns up the road for Pell's militia, Maelis in white with the ash stopping, Ulla's stone boars as a
 * line of dust thundering in, Dorn's carts in sparks and steam, Yorra's boats as lanterns lit along the land's edge,
 * Orla's wayfarers' horns as rings over the hills.
 *
 * Draw calls: one glow layer. The entrances are the shared vfx.
 */

/** How long an entrance's lights linger (seconds). */
const LINGER = 7;
const PALE = new THREE.Color('#b8d0ff');
const LANTERN = new THREE.Color('#ffb070');
const SEA = new THREE.Color('#9fd8ff');

interface Entrance {
  npc: string;
  /** Where they came in and the way toward the keep (world). */
  at: THREE.Vector3;
  dx: number;
  dz: number;
  t0: number;
}

export class BellNightLayer {
  readonly group = new THREE.Group();
  private glow = new GlowLayer(160);
  private sky: Sky;
  private entrances: Entrance[] = [];
  private now = 0;
  /** The hour drawn (eased): how deep the night, how far the dawn. */
  private night = -1;
  private dawnK = -1;
  private v = new THREE.Vector3();
  private c = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    private dusk: boolean,
  ) {
    this.group.name = 'bell-night';
    this.group.add(this.glow.mesh);
    this.sky = captureSky(ctx);
    this.paint(dusk ? 0.6 : 1, 0);
  }

  private paint(night: number, dawn: number): void {
    // Repaint only when it moved enough to see (a handful of colour sets; measured: no cost against the layer off, docs/RENDIMIENTO.md).
    if (Math.abs(night - this.night) < 0.01 && Math.abs(dawn - this.dawnK) < 0.01) return;
    this.night = night;
    this.dawnK = dawn;
    paintHour(this.ctx, this.sky, night, dawn);
  }

  /** Every fresh sim tick: the allies coming in. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    for (const e of s._events ?? []) {
      if (e.t !== 'ally') continue;
      const at = this.ctx.at(e.x, e.y, 0.1);
      const d = Math.hypot(at.x, at.z) || 1;
      const en: Entrance = { npc: e.npc, at, dx: -at.x / d, dz: -at.z / d, t0: this.now };
      this.entrances.push(en);
      const up = at.clone().setY(at.y + 0.9);
      const g = at.clone().setY(at.y + 0.05);
      switch (e.npc) {
        case 'gaspar':
          // On guard at the gate, as always: a cold flare, a ring of pale light, motes rising off him.
          this.ctx.flare(up, '#b8d0ff', 1.8, 18);
          this.ctx.vfx.ring(g, 0.3, 2.4, '#b8d0ff', 22, 0.7);
          this.ctx.vfx.burst(up, { n: low ? 8 : 20, color: '#dfe8ff', speed: 0.02, up: 0.05, size: 0.07, life: 40, gravity: -0.002, spread: 0.5 });
          break;
        case 'maelis':
          // The ash stops: a white flare and a wide, slow ring of clear air.
          this.ctx.flare(up, '#f4f5f7', 1.8, 18);
          this.ctx.vfx.ring(g, 0.5, 3.5, '#f4f5f7', 26, 0.45);
          this.ctx.vfx.burst(up, { n: low ? 8 : 18, color: '#ffffff', speed: 0.03, up: 0.04, size: 0.06, life: 34, gravity: -0.001, spread: 0.8 });
          break;
        case 'pell':
          this.ctx.flare(up, '#ffb070', 2.2, 16);
          break;
        case 'ulla':
          // The stampede: a horn, and the ground shakes as the boars come down.
          this.ctx.shake(0.22);
          this.ctx.vfx.ring(g, 0.4, 3, '#9a947e', 18, 0.6);
          break;
        case 'dorn':
          // The carts: iron sparks and a hiss of steam.
          this.ctx.vfx.burst(up, { n: low ? 10 : 26, color: '#ffc27a', speed: 0.08, up: 0.06, size: 0.04, life: 16, gravity: 0.01, spread: 0.4 });
          this.ctx.vfx.burst(up, { n: low ? 4 : 10, color: '#c4c8d2', speed: 0.02, up: 0.05, size: 0.35, life: 34, smoke: true, spread: 0.6 });
          break;
        case 'yorra':
          this.ctx.flare(up, '#9fd8ff', 2.4, 18);
          break;
        case 'orla':
          // The wayfarers' horns from the hills: rings rolling in, leaves of the wind.
          this.ctx.vfx.ring(g, 0.3, 3.2, '#8fd0a0', 20, 0.6);
          this.ctx.vfx.ring(g, 0.3, 2, '#d8f0dc', 14, 0.5);
          break;
      }
    }
    // The stampede and the carts keep coming a moment: dust (and sparks) rolling in toward the keep.
    for (const en of this.entrances) {
      const age = this.now - en.t0;
      if (age > 2.4 || (en.npc !== 'ulla' && en.npc !== 'dorn')) continue;
      if (s.tick % (low ? 3 : 2) !== 0) continue;
      const k = age / 2.4;
      const side = Math.sin(age * 9) * 0.8;
      const p = this.v.set(en.at.x + en.dx * k * 4 - en.dz * side, 0, en.at.z + en.dz * k * 4 + en.dx * side);
      p.y = this.ctx.ground(p.x, p.z) + 0.2;
      if (en.npc === 'ulla') this.ctx.vfx.burst(p, { n: low ? 2 : 4, color: '#9a947e', speed: 0.03, up: 0.03, size: 0.4, life: 26, smoke: true, spread: 0.5 });
      else this.ctx.vfx.burst(p, { n: 2, color: '#ffc27a', speed: 0.05, up: 0.04, size: 0.04, life: 10, gravity: 0.01, spread: 0.3 });
    }
  }

  /** Every redraw: the hour, the entrances' lights, Gaspar's pale glow, each ally's name over their head. */
  draw(s: BattleState): void {
    // Dusk: the night deepens over the first minutes. Dawn: the sky warms as the Marea thins out.
    if (this.dusk) this.paint(0.6 + 0.4 * Math.min(1, s.waveT / (12 * 90)), 0);
    else {
      const left = s.wave.spawns.length ? s.wave.next / s.wave.spawns.length : 0;
      const alive = s.enemies.length;
      const k = Math.min(1, left * 0.6 + (left >= 1 ? 0.4 * (1 - Math.min(1, alive / 60)) : 0));
      this.paint(1, s.result === 'won' ? 1 : k);
    }
    this.glow.begin();
    for (let i = this.entrances.length - 1; i >= 0; i--) {
      const en = this.entrances[i] as Entrance;
      const age = this.now - en.t0;
      if (age > LINGER) {
        this.entrances.splice(i, 1);
        continue;
      }
      const fade = Math.min(1, age * 2) * Math.min(1, (LINGER - age) / 2);
      if (en.npc === 'pell') {
        // A line of lanterns up the road, lit one after another toward the keep.
        for (let k = 0; k < 7; k++) {
          if (age < k * 0.25) break;
          const x = en.at.x + en.dx * k * 0.7;
          const z = en.at.z + en.dz * k * 0.7;
          const y = this.ctx.ground(x, z) + 0.75;
          const fl = 0.85 + 0.15 * Math.sin(this.now * 7 + k * 2.1);
          this.glow.add(x, y, z, LANTERN, 0.35, 0.8 * fade * fl);
          this.glow.add(x, y, z, LANTERN, 1.4, 0.2 * fade * fl);
        }
      } else if (en.npc === 'yorra') {
        // The boats' lanterns along the land's edge round where she came in.
        const a0 = Math.atan2(en.at.z, en.at.x);
        const r = Math.hypot(en.at.x, en.at.z) + 1.2;
        for (let k = -5; k <= 5; k++) {
          if (age < Math.abs(k) * 0.18) continue;
          const a = a0 + k * 0.09;
          const x = Math.cos(a) * r;
          const z = Math.sin(a) * r;
          const y = this.ctx.ground(x, z) + 0.4 + 0.05 * Math.sin(this.now * 2 + k);
          this.glow.add(x, y, z, LANTERN, 0.3, 0.75 * fade);
          this.glow.add(x, y - 0.3, z, SEA, 1.6, 0.14 * fade);
        }
      }
    }
    // Gaspar, a spectre among the living: a cold glow on him (their names are the battle's own tags).
    for (const h of s.heroes) {
      if (h.pid !== 'npc:gaspar' || h.downTicks > 0) continue;
      const p = this.ctx.pos(h.id);
      if (p) this.glow.add(p.x, p.y + 0.7, p.z, this.c.copy(PALE), 1.3, 0.2 + 0.06 * Math.sin(this.now * 2.5));
    }
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  /** Whether this is the dawn (the last fight of the night). */
  get dawn(): boolean {
    return !this.dusk;
  }

  /**
   * The night won at dawn (the end of all the sagas): the bell's last stroke goes out from the keep as a cold flare and
   * rings of pale light rolling over the whole board, and motes rising off it into the morning. The vfx of always.
   */
  finale(): void {
    const top = this.ctx.at(0, 0, 3.8);
    this.ctx.flare(top, '#cfe3ff', 3.6, 50);
    const g = this.ctx.at(0, 0, 0.1);
    for (let i = 0; i < 4; i++) this.ctx.vfx.ring(g.clone().setY(g.y + i * 0.02), 0.6, 6 + i * 4, i % 2 ? '#ffd6c8' : '#cfe3ff', 40 + i * 14, 0.6 - i * 0.1);
    this.ctx.vfx.burst(top, { n: this.ctx.low ? 14 : 36, color: '#e8f0ff', speed: 0.025, up: 0.03, size: 0.08, life: 90, gravity: -0.001, spread: 2.5 });
  }

  dispose(): void {
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
